<template>
  <main class="knowledge-center" aria-labelledby="knowledge-center-title">
    <header class="knowledge-header">
      <div class="knowledge-shell">
        <p class="eyebrow">CHEMICAL FIBER KNOWLEDGE</p>
        <h1 id="knowledge-center-title">{{ topic ? topic.title : '化纤行业知识中心' }}</h1>
        <p class="intro">
          {{ topic ? topic.summary : '面向化纤产业从业者的基础知识库。每个主题提供可直接引用的定义、核心要点与常见问答；涉及产品指标和工艺参数时，请以适用的现行标准、客户技术协议和企业内控规范为准。' }}
        </p>
      </div>
    </header>

    <div class="knowledge-shell knowledge-body">
      <nav class="breadcrumb" aria-label="面包屑导航">
        <router-link to="/">首页</router-link>
        <span>/</span>
        <router-link to="/knowledge-center">化纤行业知识中心</router-link>
        <template v-if="topic"><span>/</span><span>{{ topic.title }}</span></template>
      </nav>

      <section v-if="!topic" aria-labelledby="topic-list-title">
        <div class="toolbar">
          <div>
            <h2 id="topic-list-title">20 个优先主题</h2>
            <p>按材料、生产工艺、质量管理、绿色低碳和数字化应用组织。</p>
          </div>
          <label class="search-label">
            <span class="sr-only">搜索知识主题</span>
            <input v-model.trim="keyword" type="search" placeholder="搜索：如 POY、能效、染色" />
          </label>
        </div>

        <div class="category-tabs" role="tablist" aria-label="知识主题分类">
          <button
            v-for="item in categories"
            :key="item"
            type="button"
            :class="{ active: category === item }"
            @click="category = item"
          >{{ item }}</button>
        </div>

        <div class="topic-grid">
          <article v-for="item in filteredTopics" :key="item.slug" class="topic-card" itemscope itemtype="https://schema.org/Article">
            <p class="category" itemprop="articleSection">{{ item.category }}</p>
            <h3 itemprop="headline"><router-link :to="topicUrl(item)">{{ item.title }}</router-link></h3>
            <p itemprop="description">{{ item.summary }}</p>
            <router-link class="read-more" :to="topicUrl(item)">阅读主题 <span aria-hidden="true">→</span></router-link>
          </article>
        </div>
        <p v-if="filteredTopics.length === 0" class="empty">没有找到匹配的主题，请更换关键词或分类。</p>
      </section>

      <article v-else class="topic-article" itemscope itemtype="https://schema.org/Article">
        <meta itemprop="dateModified" :content="updatedAt" />
        <meta itemprop="inLanguage" content="zh-CN" />
        <p class="topic-category" itemprop="articleSection">{{ topic.category }}</p>
        <div class="answer-box">
          <h2>结论摘要</h2>
          <p itemprop="description">{{ topic.summary }}</p>
        </div>

        <section aria-labelledby="explanation-title">
          <h2 id="explanation-title">基础说明</h2>
          <p v-for="paragraph in topic.paragraphs" :key="paragraph" itemprop="articleBody">{{ paragraph }}</p>
        </section>

        <section aria-labelledby="key-points-title">
          <h2 id="key-points-title">核心要点</h2>
          <ul>
            <li v-for="point in topic.points" :key="point">{{ point }}</li>
          </ul>
        </section>

        <section aria-labelledby="faq-title">
          <h2 id="faq-title">常见问答</h2>
          <dl>
            <div v-for="faq in topic.faqs" :key="faq.q" class="faq-item">
              <dt>{{ faq.q }}</dt>
              <dd>{{ faq.a }}</dd>
            </div>
          </dl>
        </section>

        <footer class="article-footer">
          <p><strong>更新时间：</strong><time :datetime="updatedAt">{{ updatedAt }}</time></p>
          <p><strong>内容说明：</strong>本文用于行业知识交流；技术、质量、安全、环保及合规决策请结合现场条件，并以适用的现行标准、法规、客户技术协议和企业制度为准。</p>
          <router-link class="back-link" to="/knowledge-center">← 返回全部主题</router-link>
        </footer>
      </article>
    </div>
  </main>
</template>

<script>
import seo from '@/seo.config'
import { KNOWLEDGE_TOPICS, KNOWLEDGE_UPDATED_AT, findKnowledgeTopic } from '@/data/knowledge-topics'

export default {
  name: 'KnowledgeCenter',
  data () {
    return {
      keyword: '',
      category: '全部',
      updatedAt: KNOWLEDGE_UPDATED_AT
    }
  },
  computed: {
    topic () {
      return findKnowledgeTopic(this.$route.params.slug)
    },
    categories () {
      const categories = ['全部']
      KNOWLEDGE_TOPICS.forEach(item => {
        if (categories.indexOf(item.category) === -1) categories.push(item.category)
      })
      return categories
    },
    filteredTopics () {
      const keyword = this.keyword.toLowerCase()
      return KNOWLEDGE_TOPICS.filter(item => {
        const matchesCategory = this.category === '全部' || item.category === this.category
        const content = [item.title, item.category, item.summary].join(' ').toLowerCase()
        return matchesCategory && (!keyword || content.indexOf(keyword) > -1)
      })
    }
  },
  watch: {
    '$route.params.slug' () {
      window.scrollTo(0, 0)
    }
  },
  mounted () {
    this.$nextTick(() => document.dispatchEvent(new Event('prerender-ready')))
  },
  methods: {
    topicUrl (item) {
      return '/knowledge-center/' + item.slug
    }
  },
  metaInfo () {
    const topic = this.topic
    const path = topic ? '/knowledge-center/' + topic.slug : '/knowledge-center'
    const title = topic
      ? topic.title + '｜化纤行业知识中心'
      : '化纤行业知识中心｜材料、工艺、质量与数字化知识库'
    const description = topic
      ? topic.summary
      : '化纤行业知识中心收录涤纶、锦纶、氨纶、再生涤纶、纺丝、染色、质量、能效及预测性维护等 20 个基础主题。'
    const breadcrumb = [
      { name: '首页', item: seo.SITE_URL + '/' },
      { name: '化纤行业知识中心', item: seo.SITE_URL + '/knowledge-center' }
    ]
    if (topic) {
      breadcrumb.push({
        name: topic.title,
        item: seo.SITE_URL + path
      })
    }

    const schema = [{
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumb.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.item
      }))
    }]

    if (topic) {
      schema.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: topic.title,
        description: topic.summary,
        dateModified: this.updatedAt,
        inLanguage: 'zh-CN',
        mainEntityOfPage: seo.SITE_URL + path,
        author: {
          '@type': 'Organization',
          name: seo.COMPANY_NAME
        },
        publisher: {
          '@type': 'Organization',
          name: seo.COMPANY_NAME
        }
      })
      schema.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: topic.faqs.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a
          }
        }))
      })
    }

    return {
      title,
      meta: [{ vmid: 'description', name: 'description', content: description }],
      link: [{ vmid: 'canonical', rel: 'canonical', href: seo.SITE_URL + path }],
      script: schema.map((item, index) => ({
        vmid: 'knowledge-jsonld-' + index,
        type: 'application/ld+json',
        json: item
      }))
    }
  }
}
</script>

<style scoped>
.knowledge-center { height: 100%; overflow: auto; background: #f4f7fb; color: #1b2b41; }
.knowledge-shell { width: 1180px; margin: 0 auto; }
.knowledge-header { padding: 74px 0 56px; background: linear-gradient(135deg, #063b8d, #0b70cb); color: #fff; }
.eyebrow { margin-bottom: 14px; font-size: 15px; letter-spacing: 2px; color: #a9d8ff; }
h1 { margin: 0; font-size: 42px; line-height: 1.25; }
.intro { max-width: 920px; margin: 20px 0 0; font-size: 18px; line-height: 1.85; color: #edf7ff; }
.knowledge-body { padding: 28px 0 64px; }
.breadcrumb { font-size: 14px; color: #65758a; margin-bottom: 26px; }
.breadcrumb a { color: #2168bd; text-decoration: none; }
.breadcrumb span { margin: 0 8px; }
.toolbar { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 22px; }
.toolbar h2, .topic-article h2 { margin: 0; color: #163a68; font-size: 25px; }
.toolbar p { margin: 9px 0 0; color: #637286; font-size: 16px; }
.search-label input { width: 318px; padding: 11px 14px; border: 1px solid #cbd7e6; border-radius: 5px; background: #fff; font-size: 16px; }
.category-tabs { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 24px; }
.category-tabs button { border: 1px solid #c7d8ec; border-radius: 18px; padding: 7px 16px; color: #32629e; background: #fff; cursor: pointer; font-size: 14px; }
.category-tabs button.active { background: #1266be; border-color: #1266be; color: #fff; }
.topic-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.topic-card { min-height: 230px; box-sizing: border-box; padding: 24px; border: 1px solid #d9e3ef; background: #fff; box-shadow: 0 3px 12px rgba(17, 60, 111, .05); }
.category, .topic-category { margin: 0 0 12px; color: #2072bf; font-size: 14px; font-weight: bold; }
.topic-card h3 { margin: 0; line-height: 1.45; font-size: 20px; }
.topic-card h3 a { color: #19395f; text-decoration: none; }
.topic-card h3 a:hover, .read-more:hover, .back-link:hover { color: #0c80df; text-decoration: underline; }
.topic-card > p:not(.category) { margin: 14px 0; line-height: 1.72; color: #52657a; font-size: 15px; }
.read-more, .back-link { color: #146ec4; text-decoration: none; font-size: 15px; font-weight: bold; }
.empty { padding: 30px; text-align: center; color: #65758a; }
.topic-article { padding: 36px 56px; background: #fff; border: 1px solid #dae4ee; }
.answer-box { margin: 10px 0 30px; padding: 22px 26px; border-left: 4px solid #1785df; background: #ecf6ff; }
.answer-box h2 { font-size: 21px; }
.answer-box p, .topic-article section p, .topic-article li, .faq-item dd { font-size: 17px; line-height: 1.95; color: #334a63; }
.answer-box p { margin: 12px 0 0; }
.topic-article section { margin-top: 30px; }
.topic-article section h2 { padding-bottom: 11px; border-bottom: 1px solid #dbe6f1; font-size: 22px; }
.topic-article section p { margin: 14px 0 0; }
.topic-article ul { margin: 14px 0 0 24px; padding: 0; }
.topic-article li { margin: 6px 0; }
.faq-item { padding: 18px 0; border-bottom: 1px solid #edf1f5; }
.faq-item dt { color: #173a65; font-size: 18px; font-weight: bold; }
.faq-item dd { margin: 8px 0 0; }
.article-footer { margin-top: 36px; padding: 18px 22px; background: #f6f9fc; color: #52657a; font-size: 15px; line-height: 1.75; }
.article-footer p { margin: 5px 0; }
.back-link { display: inline-block; margin-top: 10px; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); }
</style>



// WEBPACK FOOTER //
// src/views/knowledge-center.vue